"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Spinner } from "@/components/Spinner";
import { useRouter } from "next/navigation";
import { captureLead } from "@/lib/lead-actions";
import { capture } from "@/lib/analytics";
import { hasSessionCookie } from "@/lib/auth-client";

// ─────────────────────────────────────────────────────────────────────────────
// The email gate (§19 / brief) — every path into the builder passes through it.
// Behaviour is a 1:1 port of the reference's JS: localStorage skip, ESC and
// backdrop close, inline validation, disabled-while-submitting, category tag.
//
// Two production substitutions for the reference's demo stubs:
//   · submitGate()'s setTimeout → the captureLead server action.
//   · the "Demo note:" success text → a real redirect (live categories) or an
//     honest waitlist message (categories with no template yet).
// ─────────────────────────────────────────────────────────────────────────────

export const CAT_NAMES: Record<string, string> = {
  wedding: "Weddings",
  birthday: "Birthdays & Milestones",
  shop: "Shops & Local Business",
  restaurant: "Restaurants",
  portfolio: "Portfolios",
  coaching: "Coaching & Classes",
  any: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const STORAGE_KEY = "mysn_email";

type Source = "nav" | "hero" | "template" | "sticky" | "final" | "pricing-free";

type GateCtx = { openGate: (category: string, source: Source) => void };

const Ctx = createContext<GateCtx>({ openGate: () => {} });
export const useGate = () => useContext(Ctx);

export function GateProvider({
  children,
  liveTemplates,
}: {
  children: React.ReactNode;
  /** category → template key, for categories that actually have a template. */
  liveTemplates: Record<string, string>;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("any");
  // Which CTA opened the gate — carried through to the lead row so the
  // segmentation tag reflects where they actually clicked.
  const [source, setSource] = useState<Source>("hero");
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(false);
  const [successMsg, setSuccessMsg] = useState("Opening the builder…");
  const [successNote, setSuccessNote] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const lastFocus = useRef<HTMLElement | null>(null);

  const isWaitlist = useCallback(
    (cat: string) => cat !== "any" && !liveTemplates[cat],
    [liveTemplates],
  );

  // Shows the success pane, then either routes to the builder or — for a
  // category we haven't built — tells the truth instead of promising one.
  const succeed = useCallback(
    (email: string, cat: string) => {
      setShowSuccess(true);
      capture("builder_started", { category: cat });

      if (isWaitlist(cat)) {
        setSuccessMsg("You're on the list.");
        setSuccessNote(
          `We'll email you the moment ${CAT_NAMES[cat]} opens. Until then, you can build a Wedding site free.`,
        );
        return;
      }
      const key = cat !== "any" ? liveTemplates[cat] : null;
      setSuccessMsg(
        cat !== "any" && CAT_NAMES[cat]
          ? `Opening the ${CAT_NAMES[cat]} builder…`
          : "Opening the builder…",
      );
      setSuccessNote("");
      const builderDest = key ? `/create/${key}` : "/create";
      // /create is gated on an account (MMST-16); an anonymous visitor who
      // just handed over their email is a NEW user — send them to register
      // (prefilled), not to a bare gated route that would bounce them.
      const dest = hasSessionCookie()
        ? `${builderDest}?email=${encodeURIComponent(email)}`
        : `/register?next=${encodeURIComponent(builderDest)}&email=${encodeURIComponent(email)}`;
      setTimeout(() => router.push(dest), 700);
    },
    [isWaitlist, liveTemplates, router],
  );

  const openGate = useCallback(
    (cat: string, src: Source) => {
      setCategory(cat);
      setSource(src);
      capture("cta_click", { location: src, category: cat });

      // Returning visitors skip the gate.
      const saved =
        typeof window !== "undefined"
          ? window.localStorage.getItem(STORAGE_KEY)
          : null;
      if (saved) {
        setOpen(true);
        // A known visitor clicking a NEW waitlist category is still a new
        // signal — record it, since the category tag is the whole point.
        if (isWaitlist(cat)) {
          void captureLead({
            email: saved,
            category: cat,
            source: src,
            waitlist: true,
          });
        }
        succeed(saved, cat);
        return;
      }

      setShowSuccess(false);
      setErr(false);
      lastFocus.current = document.activeElement as HTMLElement | null;
      setOpen(true);
      setTimeout(() => inputRef.current?.focus(), 60);
    },
    [isWaitlist, succeed],
  );

  const closeGate = useCallback(() => {
    setOpen(false);
    setErr(false);
    lastFocus.current?.focus();
  }, []);

  const submitGate = useCallback(async () => {
    const v = inputRef.current?.value.trim() ?? "";
    if (!EMAIL_RE.test(v)) {
      setErr(true);
      inputRef.current?.focus();
      return;
    }
    setErr(false);
    setSubmitting(true);
    const waitlist = isWaitlist(category);
    const res = await captureLead({
      email: v,
      category,
      source,
      waitlist,
    });
    setSubmitting(false);
    if (!res.ok) {
      setErr(true);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, v);
    capture("email_captured", { category });
    succeed(v, category);
  }, [category, source, isWaitlist, succeed]);

  // ESC closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) closeGate();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [open, closeGate]);

  return (
    <Ctx.Provider value={{ openGate }}>
      {children}

      <div
        className={`overlay${open ? " open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gateTitle"
        onClick={(e) => {
          if (e.target === e.currentTarget) closeGate();
        }}
      >
        <div className="modal">
          <button className="close" aria-label="Close" onClick={closeGate}>
            ×
          </button>

          <div style={{ display: showSuccess ? "none" : "block" }}>
            <div className="gift">🎁</div>
            <h3 id="gateTitle">Your 3 free demo sites are waiting</h3>
            <p className="sub">
              Enter your email and start building — we&apos;ll save your demos so
              you can come back anytime.
            </p>
            {category !== "any" && (
              <div className="catline">
                ✨ Starting with: {CAT_NAMES[category]}
              </div>
            )}
            <label htmlFor="email">Email address</label>
            <input suppressHydrationWarning
              type="email"
              id="email"
              ref={inputRef}
              placeholder="you@example.com"
              autoComplete="email"
              className={err ? "err" : undefined}
              onKeyDown={(e) => {
                if (e.key === "Enter") void submitGate();
              }}
            />
            <div className={`err-msg${err ? " show" : ""}`}>
              Please enter a valid email address.
            </div>
            <button
              className="btn btn-primary"
              disabled={submitting}
              onClick={() => void submitGate()}
            >
              {submitting ? <Spinner tone="ink" label="Saving…" /> : "Start Building Free →"}
            </button>
            <p className="micro">
              No card required · No spam · Your demos are saved to your email
            </p>
          </div>

          <div className={`success${showSuccess ? " show" : ""}`}>
            <div className="big">🚀</div>
            <h3>You&apos;re in!</h3>
            <p className="sub">{successMsg}</p>
            {successNote && <div className="note">{successNote}</div>}
          </div>
        </div>
      </div>
    </Ctx.Provider>
  );
}

/** Any CTA that must pass through the gate. */
export function GateButton({
  category = "any",
  source,
  className = "btn btn-primary",
  style,
  children,
}: {
  category?: string;
  source: Source;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const { openGate } = useGate();
  return (
    <button
      className={className}
      style={style}
      onClick={() => openGate(category, source)}
    >
      {children}
    </button>
  );
}
